import { Component, effect, ElementRef, HostListener, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { DialogContainerDirective } from '@progress/kendo-angular-dialog';
import { AuthService } from '@auth0/auth0-angular';
import {
  ApiHttpService,
  ApplicationUpdateService,
  ApplicationUserService,
  SessionWatchService,
  TranslationLoaderService,
  WebcamService,
} from 'shared';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject, switchMap } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { AppBarComponent } from '@stock-core/components/app-bar/app-bar.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { WebcamImage, WebcamModule } from 'ngx-webcam';
import { ButtonComponent } from '@progress/kendo-angular-buttons';

@Component({
  selector: 'rc-stock-app',
  standalone: true,
  imports: [
    RouterOutlet,
    DialogContainerDirective,
    AppBarComponent,
    AsyncPipe,
    LoaderComponent,
    WebcamModule,
    NgClass,
    ButtonComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnDestroy {
  #applicationUpdateService = inject(ApplicationUpdateService);
  #authService = inject(AuthService);
  #apiService = inject(ApiHttpService);
  #applicationUserService = inject(ApplicationUserService);
  #webcamService = inject(WebcamService);
  #loaderService = inject(TranslationLoaderService);
  #sessionWatch = inject(SessionWatchService);
  user = toSignal(this.#applicationUserService.user$);
  /** The signed-out page is public: it renders on its own, without the app bar or the loader. */
  isOnSignedOutPage = this.#sessionWatch.isOnSignedOutPage;
  isLoading$ = this.#loaderService.loading$;
  camData = signal<MediaStream | null>(null);
  appBar = viewChild('appBar', { read: ElementRef });
  appBarHeight = signal('0px');
  private resizeObserver: ResizeObserver | null = null;
  width: number;
  height: number;
  trigger = new Subject<void>();

  capture: (webcamImage: WebcamImage) => void;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkOrientation();
    const value = window.screen.width > window.screen.height ? window.screen.width : window.screen.height;
    this.width = value;
    this.height = value;
  }

  constructor() {
    effect(() => {
      const el = this.appBar()?.nativeElement;
      if (el) {
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver((entry) => {
          this.appBarHeight.set(`${el.offsetHeight}px`);
        });

        this.resizeObserver.observe(el);
      }
    });

    this.onResize();
    this.#webcamService.capture.subscribe((value) => {
      if (value) this.capture = value;
    });
    this.#webcamService.camData.subscribe((value) => {
      const previous = this.camData();
      if (previous && previous !== value) previous.getTracks().forEach((track) => track.stop());
      this.camData.set(value);
    });

    this.#applicationUpdateService.checkAndApplyIfReady().then(() => {
      this.#sessionWatch.watch();
      // The signed-out page is public: no user to load, no api cookie to ask for
      if (this.isOnSignedOutPage()) return;

      this.#applicationUserService.setApplicationUser();
      this.#authService.idTokenClaims$
        .pipe(
          filter((token) => !!token),
          first(),
          switchMap(() => this.#apiService.get('/cookie')),
        )
        .subscribe();
    });
  }

  closeCamera() {
    this.#webcamService.camData.next(null);
  }

  captureImage() {
    this.trigger.next();
  }

  get trigger$(): Observable<void> {
    return this.trigger.asObservable();
  }

  private checkOrientation(): void {
    this.#webcamService.isLandscape.set(window.innerWidth > window.innerHeight);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }
}
